/**
 * ReserveCommissariatComptesService — réserves CAC (`/api/v1/reserves-commissariat-aux-comptes/`).
 *
 * Lecture (liste + détail) et PATCH de **validation DSE** (catégorie, reformulation,
 * récurrence). La création/remplacement des réserves se fait en **nested** via
 * `CommissariatAuxComptesService` (champ `reserves[]`), pas ici.
 */

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { DrfPaginatedResponse } from '../../../../core/models/api.model';
import { buildHttpParams, QueryFilters } from '../../../../shared/core/services/pagination.util';
import {
    PatchedReserveCommissariatAuxComptesPartialUpdateRequest,
    ReserveCommissariatAuxComptes,
    ReserveCommissariatAuxComptesPartialUpdateResponse,
} from '../models/cac-api.model';

/** Filtres serveur exposés par `GET /reserves-commissariat-aux-comptes/`. */
export type ReserveCacListFilters = QueryFilters & {
    page?: number;
    per_page?: number;
    societe?: string;
    /** Filtre par exercice (commissariat) — nom serveur `commissariat_aux_comptes_id`. */
    commissariat_aux_comptes_id?: string;
    categorie__code?: string;
    categorie__code__in?: string;
    recurrence?: boolean;
    montant?: number;
    montant__gte?: number;
    montant__lte?: number;
};

@Injectable({ providedIn: 'root' })
export class ReserveCommissariatComptesService {
    private readonly http = inject(HttpClient);
    private readonly endpoint = `${environment.apiUrl}/reserves-commissariat-aux-comptes/`;

    list(filters: ReserveCacListFilters = {}): Observable<DrfPaginatedResponse<ReserveCommissariatAuxComptes>> {
        return this.http.get<DrfPaginatedResponse<ReserveCommissariatAuxComptes>>(this.endpoint, {
            params: buildHttpParams(filters),
        });
    }

    retrieve(id: string): Observable<ReserveCommissariatAuxComptes> {
        return this.http.get<ReserveCommissariatAuxComptes>(`${this.endpoint}${id}/`);
    }

    /**
     * PATCH — validation DSE (categorie_id, reformulation, recurrence).
     * La réponse est **tronquée** par le serializer de mise à jour partielle
     * (id, categorie, reformulation, recurrence) : elle ne peut pas être mappée en `ReserveCac`.
     */
    partialUpdate(
        id: string,
        payload: PatchedReserveCommissariatAuxComptesPartialUpdateRequest,
    ): Observable<ReserveCommissariatAuxComptesPartialUpdateResponse> {
        return this.http.patch<ReserveCommissariatAuxComptesPartialUpdateResponse>(`${this.endpoint}${id}/`, payload);
    }
}
